import { Button, View } from "@/components/ui";
import { FormInputPhoneNumber } from "@/components/shared/input-phone-number/controlled";
import { ErrorMessage } from "@/components/shared/login-register/error-message";
import { useProfileState } from "@/store/profile";
import { ChangePhoneNumberModalEnum } from "@/types/profile";
import type { ChangePhoneNumberFormData } from "@/store/profile/forms/useChangePhoneNumberForm";
import { useChangePhoneNumberForm } from "@/store/profile/forms/useChangePhoneNumberForm";
import { useAuthState } from "@/store/auth";
import { useEffect } from "react";
import { useClientVerifyPhoneNumberStore } from "@/store/auth/clientVerifyPhoneNumber";
import { FormProvider } from "react-hook-form";
import { ensurePhoneNumberWithPlus } from "@/lib/helpers/string";

export const ChangeNumberForm = () => {
  const { data } = useAuthState();
  const { setPhone, setUpdatedPhone } = useProfileState();
  const { userInfo } = data;
  const contact = userInfo.user.phoneNumber;
  const currentPhone = contact ? ensurePhoneNumberWithPlus(contact) : "";
  const formValues: ChangePhoneNumberFormData = {
    currentPhoneNumber: currentPhone,
    newPhoneNumber: "+65",
  };

  const phoneNumberFormMethods = useChangePhoneNumberForm(formValues);
  const { handleSubmit, watch, setValue } = phoneNumberFormMethods;

  const { showModal } = useProfileState();

  const {
    fetch: clientVerifyPhoneNumber,
    loading: clientVerifyPhoneNumberLoading,
    error: clientVerifyPhoneNumberError,
    clear: clearClientVerifyPhoneNumber,
  } = useClientVerifyPhoneNumberStore();

  // Clear errors when component unmounts (modal closes)
  useEffect(() => {
    return () => {
      clearClientVerifyPhoneNumber();
    };
  }, []);

  // Clear errors when user changes the new phone number
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === "newPhoneNumber") {
        clearClientVerifyPhoneNumber();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // When at the first time the contact is an empty string because there is a delay,
  // update the form when the contact exists
  useEffect(() => {
    if (contact) {
      const formattedPhoneNumber = ensurePhoneNumberWithPlus(contact);
      setValue("currentPhoneNumber", formattedPhoneNumber);
      setPhone(contact);
    }
  }, [contact, setValue, setPhone]);

  const onSubmitFormData = handleSubmit((data: ChangePhoneNumberFormData) => {
    const newPhone = ensurePhoneNumberWithPlus(data.newPhoneNumber);
    setUpdatedPhone(newPhone);
    clientVerifyPhoneNumber(
      {
        requestPayload: {
          input: {
            phone: newPhone,
            onSignUp: true,
            enableWhatsappOtp: true,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      if (res.error) {
        return;
      }

      showModal(
        ChangePhoneNumberModalEnum.CHANGE_PHONE_NUMBER_OTP_VERIFICATION,
      );
    });
  });

  return (
    <FormProvider {...phoneNumberFormMethods}>
      <View className="flex flex-col gap-6">
        <FormInputPhoneNumber
          name="currentPhoneNumber"
          label="Current Phone Number"
          disabled
        />
        <FormInputPhoneNumber name="newPhoneNumber" label="New Phone Number" />
        {clientVerifyPhoneNumberError && (
          <ErrorMessage errorMessage={clientVerifyPhoneNumberError} />
        )}
        <Button
          variant="primary"
          color="CTA"
          fullWidth="full"
          loading={clientVerifyPhoneNumberLoading}
          onClick={onSubmitFormData}
          disabled={clientVerifyPhoneNumberLoading}
          children="Continue"
        />
      </View>
    </FormProvider>
  );
};
